import { Box, Text } from "@chakra-ui/react"
import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"
import { useMemo } from "react"

import { DiscussionResponseFragment } from "@pathwright/graphql-client/src/generated"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"
import { canAdministrate } from "../../user/permissions"
import { useResponseDepthIndent } from "./ResponseListItem"

type ResponseListItemArchivedProps = {
  response: DiscussionResponseFragment
  allResponses: DiscussionResponseFragment[]
}

/**
 * React conversion of response-list-item-archived.js
 * Displays archived/deleted discussion responses
 *
 * Original Marionette view functionality:
 * - Shows "Response deleted" message for archived responses
 * - Visibility logic: hide archived responses with no children, unless user is admin
 * - Finds non-archived child responses recursively
 * - Applies depth-based styling
 *
 * Note: The complex visibility logic from _hideArchivedResponses is preserved
 */
function ResponseListItemArchived({
  response,
  allResponses
}: ResponseListItemArchivedProps) {
  const { t } = useTranslate()
  const pwContext = usePathwrightContext()

  const shouldRender = useMemo(() => {
    // Admin can see all archived responses.
    if (canAdministrate(pwContext)) {
      return true
    }

    // Replicates the _hideArchivedResponses logic from the original view
    const hasNonArchivedChildResponses = allResponses.some((child) => {
      function hasUnarchivedChild(
        parent: DiscussionResponseFragment,
        testChild: DiscussionResponseFragment
      ): boolean {
        return (
          parent.id === testChild.parent_id &&
          (!testChild.archived_time ||
            // This child is archived, but this child's children may not be
            allResponses.some((grandchild) =>
              hasUnarchivedChild(testChild, grandchild)
            ))
        )
      }

      return hasUnarchivedChild(response, child)
    })

    // Anyone can see archived responses that have child responses
    return hasNonArchivedChildResponses
  }, [response, allResponses])

  const depthIndent = useResponseDepthIndent(response)

  if (!shouldRender) {
    return null
  }

  return (
    <Box
      className={`response-list-item-archived-view response-depth-${response.depth}`}
      pl={depthIndent}
    >
      <Text color="gray.500" fontStyle="italic">
        {t("discussion.list_item.archived")}
      </Text>
    </Box>
  )
}

export default ResponseListItemArchived
